import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useLocalization } from "../../hooks/useLocalization";
import { Localization } from "../../types/types";
import { adsenseConfig, isAdsenseConfigured } from "../../ads/config";
import { loadAdSenseScript, pushAdSenseSlot } from "../../ads/loadAdSense";
import { AD_PLACEMENTS, getPlacementSlotId, type AdPlacementId } from "../../ads/placements";

interface AdPlacementProps {
  placement: AdPlacementId;
  className?: string;
}

const getPlacementLabel = (placement: AdPlacementId, locale: Localization): string => {
  const meta = AD_PLACEMENTS[placement];
  return locale === Localization.Finnish ? meta.labelFi : meta.labelEn;
};

export const AdPlacement = ({ placement, className }: AdPlacementProps) => {
  const { locale, strings } = useLocalization();
  const location = useLocation();
  const insRef = useRef<HTMLModElement>(null);
  const [liveError, setLiveError] = useState(false);

  const isAdminRoute = /admin/i.test(location.pathname);
  const meta = AD_PLACEMENTS[placement];
  const slotId = getPlacementSlotId(placement);
  const label = getPlacementLabel(placement, locale);
  const usePreview = adsenseConfig.preview || !isAdsenseConfigured() || !slotId;
  const isSidebar = meta.format === "sidebar";

  useEffect(() => {
    if (!adsenseConfig.enabled || isAdminRoute || usePreview) return;

    let cancelled = false;

    void loadAdSenseScript()
      .then(() => {
        if (cancelled) return;
        pushAdSenseSlot(insRef.current);
        setLiveError(false);
      })
      .catch(() => {
        if (!cancelled) setLiveError(true);
      });

    return () => {
      cancelled = true;
    };
  }, [isAdminRoute, usePreview, placement]);

  if (!adsenseConfig.enabled || isAdminRoute) {
    return null;
  }

  const wrapperBase = `m-0 w-full${isSidebar ? " mx-auto max-w-40" : ""}`;

  if (usePreview) {
    const hint =
      meta.format === "sidebar"
        ? strings.ADS.PREVIEW_SIDEBAR_HINT
        : strings.ADS.PREVIEW_MOBILE_HINT;

    return (
      <div
        className={`flex justify-center ${wrapperBase}${className ? ` ${className}` : ""}`}
        role="note"
        aria-label={label}
      >
        <div
          className={`box-border w-full max-w-full border border-dashed border-[rgba(123,147,255,0.45)] bg-[rgba(123,147,255,0.06)] text-center text-[#b8bdd4] ${
            isSidebar ? "min-h-[36rem] p-[1rem_0.65rem]" : "min-h-[60px] p-[0.85rem_1rem]"
          } rounded-[0.65rem]`}
        >
          <span className="mb-[0.35rem] inline-block rounded-full bg-[rgba(123,147,255,0.16)] px-[0.45rem] py-[0.15rem] text-[0.62rem] font-bold tracking-[0.06em] text-[#9eb0ff] uppercase">
            {strings.ADS.PREVIEW_BADGE}
          </span>
          <strong
            className={`mb-1 block font-semibold text-[#e8e8ef] ${isSidebar ? "text-[0.88rem]" : "text-[0.8rem]"}`}
          >
            {label}
          </strong>
          <p className="m-0 text-[0.72rem] leading-[1.4] text-[#8b8b9a]">{hint}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${wrapperBase}${className ? ` ${className}` : ""}`}>
      {liveError ? (
        <div
          className={`box-border w-full max-w-full border border-dashed border-[rgba(248,113,113,0.45)] bg-[rgba(248,113,113,0.06)] text-center text-[#b8bdd4] ${
            isSidebar ? "min-h-[36rem] p-[1rem_0.65rem]" : "min-h-[60px] p-[0.85rem_1rem]"
          } rounded-[0.65rem]`}
        >
          <span className="mb-[0.35rem] inline-block rounded-full bg-[rgba(123,147,255,0.16)] px-[0.45rem] py-[0.15rem] text-[0.62rem] font-bold tracking-[0.06em] text-[#9eb0ff] uppercase">
            {strings.ADS.PREVIEW_BADGE}
          </span>
          <strong
            className={`mb-1 block font-semibold text-[#e8e8ef] ${isSidebar ? "text-[0.88rem]" : "text-[0.8rem]"}`}
          >
            {label}
          </strong>
          <p className="m-0 text-[0.72rem] leading-[1.4] text-[#8b8b9a]">
            {strings.ADS.LOAD_ERROR}
          </p>
        </div>
      ) : (
        <ins
          ref={insRef}
          className={`adsbygoogle w-full ${isSidebar ? "min-h-[36rem]" : "min-h-[60px]"}`}
          style={{ display: "block" }}
          data-ad-client={adsenseConfig.clientId}
          data-ad-slot={slotId}
          data-ad-format="auto"
          data-full-width-responsive="true"
          {...(adsenseConfig.test ? { "data-adtest": "on" as const } : {})}
        />
      )}
    </div>
  );
};
